import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/use-toast';
import type { AnalyzedResult, SentimentBreakdown, SignalAlert } from '@/hooks/useAthleteSignalIntel';

export interface SignalEntity {
  id: string;
  name: string;
  type: 'self' | 'brand' | 'talent' | 'venture' | 'agent';
}

export interface SignalIntelligence {
  entities: SignalEntity[];
  resultsByEntity: Record<string, AnalyzedResult[]>;
  combinedResults: AnalyzedResult[];
  sentimentBreakdown: SentimentBreakdown;
  alerts: SignalAlert[];
  lastUpdated: Date | null;
  isLoading: boolean;
  error: string | null;
}

const signalCache = new Map<string, { data: SignalIntelligence; timestamp: number }>();
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export function useSignalIntelligence(dateRange: '7d' | '30d' | '90d' = '7d') {
  const { currentUser, accountType } = useUser();
  const { toast } = useToast();
  const [data, setData] = useState<SignalIntelligence | null>(() => {
    const cacheKey = `signal_${currentUser?.id}_${dateRange}`;
    const cached = signalCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(false);

  const buildSearchEntities = useCallback(async (): Promise<SignalEntity[]> => {
    if (!currentUser?.id) return [];

    const entities: SignalEntity[] = [];

    // Add self
    if (accountType === 'brand') {
      const { data: brand } = await supabase
        .from('brands')
        .select('company_name')
        .eq('profile_id', currentUser.id)
        .maybeSingle();
      if (brand?.company_name) {
        entities.push({ id: currentUser.id, name: brand.company_name, type: 'self' });
      }
    } else if (accountType === 'venture') {
      const { data: vc } = await supabase
        .from('venture_capitals' as any)
        .select('firm_name')
        .eq('profile_id', currentUser.id)
        .maybeSingle();
      if ((vc as any)?.firm_name) {
        entities.push({ id: currentUser.id, name: (vc as any).firm_name, type: 'self' });
      }
    } else if (accountType === 'agent') {
      const { data: agent } = await supabase
        .from('agents')
        .select('professional_name, agency_name')
        .eq('profile_id', currentUser.id)
        .maybeSingle();
      const name = agent?.agency_name || agent?.professional_name;
      if (name) {
        entities.push({ id: currentUser.id, name, type: 'self' });
      }
    } else {
      // talent — prefer claimed athlete name over display_name
      const { data: claimedAthlete } = await supabase
        .from('api_athletes')
        .select('name')
        .eq('claimed_by_profile_id', currentUser.id)
        .eq('claim_status', 'approved')
        .maybeSingle();
      
      const name = claimedAthlete?.name || currentUser.display_name;
      if (name) {
        entities.push({ id: currentUser.id, name, type: 'self' });
      }
    }

    // Add deal partners
    let deals: any[] = [];
    if (accountType === 'brand') {
      const { data: d } = await supabase
        .from('deals')
        .select('talent_profile_id, venture_profile_id')
        .eq('brand_profile_id', currentUser.id)
        .eq('status', 'active')
        .limit(10);
      deals = d || [];
    } else if (accountType === 'venture') {
      const { data: d } = await supabase
        .from('deals')
        .select('talent_profile_id, brand_profile_id')
        .eq('venture_profile_id', currentUser.id)
        .eq('status', 'active')
        .limit(10);
      deals = d || [];
    } else {
      // talent or agent
      const { data: d } = await supabase
        .from('deals')
        .select('brand_profile_id, venture_profile_id')
        .eq('talent_profile_id', currentUser.id)
        .eq('status', 'active')
        .limit(10);
      deals = d || [];
    }

    // Collect unique partner profile IDs
    const partnerIds = new Set<string>();
    for (const deal of deals) {
      if (deal.talent_profile_id && deal.talent_profile_id !== currentUser.id) partnerIds.add(deal.talent_profile_id);
      if (deal.brand_profile_id && deal.brand_profile_id !== currentUser.id) partnerIds.add(deal.brand_profile_id);
      if (deal.venture_profile_id && deal.venture_profile_id !== currentUser.id) partnerIds.add(deal.venture_profile_id);
    }

    // Fetch partner names from profiles
    if (partnerIds.size > 0) {
      const ids = Array.from(partnerIds).slice(0, 4); // cap at 4 partners (5 total with self)
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, account_type')
        .in('id', ids);

      for (const profile of profiles || []) {
        if (!profile.display_name) continue;
        let name = profile.display_name;

        // For brands, try to get company name
        if (profile.account_type === 'brand') {
          const { data: brand } = await supabase
            .from('brands')
            .select('company_name')
            .eq('profile_id', profile.id)
            .maybeSingle();
          if (brand?.company_name) name = brand.company_name;
          entities.push({ id: profile.id, name, type: 'brand' });
        } else if (profile.account_type === 'venture') {
          const { data: vc } = await supabase
            .from('venture_capitals' as any)
            .select('firm_name')
            .eq('profile_id', profile.id)
            .maybeSingle();
          if ((vc as any)?.firm_name) name = (vc as any).firm_name;
          entities.push({ id: profile.id, name, type: 'venture' });
        } else {
          entities.push({ id: profile.id, name, type: 'talent' });
        }
      }
    }

    return entities.slice(0, 5);
  }, [currentUser?.id, accountType, currentUser?.display_name]);

  const fetchSignalIntelligence = useCallback(async (forceRefresh = false) => {
    if (!currentUser?.id) return;

    const cacheKey = `signal_${currentUser.id}_${dateRange}`;
    const cached = signalCache.get(cacheKey);
    if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setData(cached.data);
      return;
    }

    setIsLoading(true);
    try {
      const entities = await buildSearchEntities();
      if (entities.length === 0) {
        const emptyData: SignalIntelligence = {
          entities: [],
          resultsByEntity: {},
          combinedResults: [],
          sentimentBreakdown: { positive: 0, neutral: 0, negative: 0, overallScore: 0, overallSentiment: 'neutral' },
          alerts: [],
          lastUpdated: new Date(),
          isLoading: false,
          error: null,
        };
        setData(emptyData);
        return;
      }

      // Fetch Google results for each entity sequentially with delay
      const resultsByEntity: Record<string, any[]> = {};
      const allSearchResults: any[] = [];

      for (const entity of entities) {
        try {
          const { data: searchData, error: searchError } = await supabase.functions.invoke('google-search-athlete', {
            body: { athleteName: entity.name, dateRange }
          });

          if (searchError) {
            console.warn(`Search failed for ${entity.name}:`, searchError);
            continue;
          }

          const results = searchData?.searchResults || [];
          resultsByEntity[entity.id] = results;
          allSearchResults.push(...results.map((r: any) => ({ ...r, _entityId: entity.id, _entityName: entity.name })));

          // Small delay between calls
          if (entities.indexOf(entity) < entities.length - 1) {
            await delay(300);
          }
        } catch (err) {
          console.warn(`Search error for ${entity.name}:`, err);
        }
      }

      if (allSearchResults.length === 0) {
        const noResultsData: SignalIntelligence = {
          entities,
          resultsByEntity: {},
          combinedResults: [],
          sentimentBreakdown: { positive: 0, neutral: 0, negative: 0, overallScore: 0, overallSentiment: 'neutral' },
          alerts: [],
          lastUpdated: new Date(),
          isLoading: false,
          error: null,
        };
        setData(noResultsData);
        signalCache.set(cacheKey, { data: noResultsData, timestamp: Date.now() });
        return;
      }

      // Distribute analysis slots fairly across entities
      const MAX_ANALYSIS = 10;
      const maxPerEntity = Math.max(1, Math.floor(MAX_ANALYSIS / entities.length));
      const balancedResults: any[] = [];
      for (const entity of entities) {
        const entityResults = (resultsByEntity[entity.id] || []).slice(0, maxPerEntity);
        balancedResults.push(...entityResults.map((r: any) => ({ ...r, _entityId: entity.id, _entityName: entity.name })));
      }
      // Fill remaining slots round-robin if any entity had fewer results
      if (balancedResults.length < MAX_ANALYSIS) {
        for (const entity of entities) {
          const already = balancedResults.filter(r => r._entityId === entity.id).length;
          const remaining = (resultsByEntity[entity.id] || []).slice(already);
          for (const r of remaining) {
            if (balancedResults.length >= MAX_ANALYSIS) break;
            balancedResults.push({ ...r, _entityId: entity.id, _entityName: entity.name });
          }
        }
      }

      const searchResultsForAnalysis = balancedResults.map(r => ({
        title: r.title,
        link: r.link,
        snippet: r.snippet,
        source: r.source,
        publishedDate: r.publishedDate,
      }));

      const entityNames = entities.map(e => e.name).join(', ');
      const { data: analysisData, error: analysisError } = await supabase.functions.invoke('analyze-athlete-signal', {
        body: { searchResults: searchResultsForAnalysis, athleteName: entityNames }
      });

      // Build analyzed results mapped back to entities
      const analyzedByEntity: Record<string, AnalyzedResult[]> = {};
      const analyzedResults: AnalyzedResult[] = (analysisData?.analyzedResults || searchResultsForAnalysis.map((r: any) => ({
        ...r, sentiment: 'neutral', sentimentScore: 0, category: 'general', priority: 'low'
      }))).map((result: AnalyzedResult, idx: number) => {
        const original = balancedResults[idx];
        const entityId = original?._entityId || entities[0]?.id;
        if (!analyzedByEntity[entityId]) analyzedByEntity[entityId] = [];
        analyzedByEntity[entityId].push(result);
        return result;
      });

      const signalResult: SignalIntelligence = {
        entities,
        resultsByEntity: analyzedByEntity,
        combinedResults: analyzedResults,
        sentimentBreakdown: analysisData?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0, overallScore: 0, overallSentiment: 'neutral' },
        alerts: analysisData?.alerts || [],
        lastUpdated: new Date(),
        isLoading: false,
        error: analysisError ? 'Sentiment analysis failed, showing raw results' : null,
      };

      setData(signalResult);
      signalCache.set(cacheKey, { data: signalResult, timestamp: Date.now() });
    } catch (error) {
      console.error('Signal intelligence error:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to fetch signal intelligence';
      toast({ title: 'Signal Intelligence Error', description: errorMsg, variant: 'destructive' });
      setData({
        entities: [],
        resultsByEntity: {},
        combinedResults: [],
        sentimentBreakdown: { positive: 0, neutral: 0, negative: 0, overallScore: 0, overallSentiment: 'neutral' },
        alerts: [],
        lastUpdated: new Date(),
        isLoading: false,
        error: errorMsg,
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentUser?.id, buildSearchEntities, toast, dateRange]);

  // Auto-fetch on mount when no cached data exists
  useEffect(() => {
    if (!data && currentUser?.id && !isLoading) {
      fetchSignalIntelligence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  return { data, isLoading, refresh: () => fetchSignalIntelligence(true) };
}
